/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023-2025 (original work) Open Assessment Technologies SA
 *
 */

import config from '@/config';
import baseRequest from 'core/fetchRequest';
import request, { getEndpointUrl } from '@/core/apiRequest/apiRequest';
import {
    CORE_FEATURES,
    FEATURE_CREATE_PUBLIC_SESSIONS,
    FEATURE_ACCESS_PUBLIC_SESSIONS,
    FEATURE_HIERARCHIES,
    FEATURE_CUSTOM_REPORTS,
    FEATURE_SESSION_TEMPLATES,
    FEATURE_CANDIDATES,
    FEATURE_TESTS,
    FEATURE_SUPPORT,
    FEATURE_STUDENT_SESSIONS,
    FEATURE_CERTIFICATION_SESSIONS,
    FEATURE_CAMPAIGNS,
    FEATURE_GROUPS,
    FEATURE_SCORING_PROJECTS
} from '@/constants/features';

/**
 * Returns specific tenant configuration
 * @param {string} subdomain
 * @returns {Promise<Response>}
 */
export const getTenantConfiguration = async subdomain => {
    const response = await baseRequest(getEndpointUrl('tenantConfiguration', { subdomain }, {}, config.baseUrls.api), {
        method: 'GET'
    });

    response.configuration ??= {};

    const featuresMap = new Map([
        [
            [FEATURE_CREATE_PUBLIC_SESSIONS, FEATURE_ACCESS_PUBLIC_SESSIONS],
            response.configuration.publicSessionsEnabled
        ],
        [[FEATURE_HIERARCHIES], response.configuration.hierarchyEnabled],
        [[FEATURE_CUSTOM_REPORTS], response.configuration.customReports?.active],
        [
            [
                FEATURE_SESSION_TEMPLATES,
                FEATURE_CERTIFICATION_SESSIONS,
                FEATURE_CAMPAIGNS,
                FEATURE_CANDIDATES,
                FEATURE_SCORING_PROJECTS
            ],
            response.configuration.certificationProduct
        ],
        [[FEATURE_GROUPS], !response.configuration.certificationProduct],
        [[FEATURE_STUDENT_SESSIONS], !response.configuration.certificationProduct],
        [[FEATURE_TESTS], response.configuration.testsEnabled],
        [[FEATURE_SUPPORT], response.configuration.supportEnabled]
    ]);

    response.configuration.features = [...featuresMap.entries()]
        .filter(([, value]) => value)
        .map(([key]) => key)
        .reduce((accumulator, currentValue) => [...accumulator, ...currentValue], [...CORE_FEATURES]);

    return response;
};

/**
 * Returns report translation map for a specific tenant
 *
 * @param {string} tenantId
 * @param {string} locale
 * @returns {Promise<Response>}
 */
export const getTenantReportTranslations = (tenantId, locale) =>
    baseRequest(getEndpointUrl('reportsTranslations', { tenantId, locale }, {}), {
        method: 'GET'
    });

/**
 * Returns configuration translation map for a specific tenant
 *
 * @param {string} tenantId
 * @param {string} locale
 * @returns {Promise<Response>}
 */
export const getTenantConfigurationsTranslations = (tenantId, locale) =>
    baseRequest(getEndpointUrl('configurationsTranslations', { tenantId, locale }, {}), {
        method: 'GET'
    });

/**
 * @param {string} tenantId
 * @returns {Promise<{}[]>}
 */
export const getTenantRoles = tenantId =>
    request(getEndpointUrl('roles', { tenantId }), {
        method: 'GET'
    });

export const getTenantCountries = tenantId =>
    baseRequest(getEndpointUrl('countries', { tenantId }), {
        method: 'GET'
    });

export const getTestCategories = tenantId =>
    baseRequest(getEndpointUrl('testCategories', { tenantId }), {
        method: 'GET'
    });
